<template>
  <Loader :show="isProductLoading" v-if="isProductLoading"></Loader>
  <MDBRow v-else>
    <MDBCol col="12" v-if="productApi">
      <MDBCard>
        <MDBCardBody>
          <div
            v-if="descriptionHtml"
            class="product-description"
            v-html="descriptionHtml"
          ></div>
        </MDBCardBody>
      </MDBCard>
    </MDBCol>
  </MDBRow>
</template>

<script setup>
import Loader from "@/components/General/Loader/index";
import { MDBRow, MDBCol, MDBCard, MDBCardBody } from "mdb-vue-ui-kit";
import { useStore } from "vuex";
import { computed } from "vue";
import { useRoute } from "vue-router";
import { pickTranslation } from "@/utils/pickTranslation";
import { markdownToHtml } from "@/utils/markdown";

const store = useStore();
const route = useRoute();

const productApi = computed(() => store.getters.getProduct);
const currentLocale = computed(() => store.getters.getCurrentLocale);
const descriptionHtml = computed(() => {
  if (!productApi.value?.description) {
    return "";
  }

  return markdownToHtml(
    pickTranslation(productApi.value.description, currentLocale.value)
  );
});
const isProductLoading = computed(() => {
  if (!productApi.value) {
    return true;
  }
  return String(productApi.value.id) !== String(route.params.id);
});

</script>

<style scoped lang="scss">
@import "@/styles/variables";

.product-description {
  font-size: 16px;
  line-height: 1.65;
  color: $darkGray;

  :deep(> *:first-child) {
    margin-top: 0;
  }

  :deep(> *:last-child) {
    margin-bottom: 0;
  }

  :deep(h1),
  :deep(h2),
  :deep(h3),
  :deep(h4),
  :deep(h5),
  :deep(h6) {
    color: $fontDark;
    font-weight: 700;
    line-height: 1.3;
    margin: 1.25em 0 0.5em;
  }

  :deep(h1) {
    font-size: 28px;
  }

  :deep(h2) {
    font-size: 24px;
  }

  :deep(h3) {
    font-size: 20px;
  }

  :deep(h4) {
    font-size: 18px;
  }

  :deep(p) {
    margin: 0 0 1em;
  }

  :deep(ul),
  :deep(ol) {
    margin: 0 0 1em;
    padding-left: 1.5em;
  }

  :deep(li) {
    margin-bottom: 0.35em;
  }

  :deep(li > ul),
  :deep(li > ol) {
    margin: 0.35em 0 0;
  }

  :deep(a) {
    color: $accentBlue;
    text-decoration: underline;

    &:hover,
    &:active {
      color: darken($accentBlue, 12%);
    }
  }

  :deep(strong),
  :deep(b) {
    font-weight: 700;
    color: $fontDark;
  }

  :deep(em),
  :deep(i) {
    font-style: italic;
  }

  :deep(blockquote) {
    margin: 0 0 1em;
    padding: 0.75em 1em;
    border-left: 4px solid $successGreen;
    background: $backgroundGray;
    color: $gray;
  }

  :deep(img) {
    display: block;
    max-width: 100%;
    height: auto;
    border-radius: 4px;
    margin: 0.75em 0;
  }

  :deep(hr) {
    border: 0;
    border-top: 1px solid $borderLightGray;
    margin: 1.5em 0;
  }

  :deep(table) {
    width: 100%;
    border-collapse: collapse;
    margin: 0 0 1em;
  }

  :deep(th),
  :deep(td) {
    border: 1px solid $borderLightGray;
    padding: 0.5em 0.75em;
    text-align: left;
    vertical-align: top;
  }

  :deep(th) {
    background: $backgroundGray;
    font-weight: 700;
  }

  :deep(code) {
    background: $backgroundGray;
    padding: 0.15em 0.4em;
    border-radius: 3px;
    font-size: 0.9em;
  }

  :deep(pre) {
    background: $backgroundGray;
    padding: 1em;
    border-radius: 4px;
    overflow-x: auto;
    margin: 0 0 1em;

    code {
      padding: 0;
      background: transparent;
    }
  }
}

:deep(.product-image-wrap),
:deep(.product-main-data) {
  height: 100%;
}

@media screen and (min-width: $smBreakpoint) {
  :deep(.product-image) {
    max-height: 300px;
  }
}

@media screen and (min-width: $mdBreakpoint) {
  :deep(.product-image) {
    max-height: 300px;
  }
}

@media screen and (min-width: $lgBreakpoint) {
  :deep(.product-image) {
    max-height: 410px;
  }
}

@media screen and (min-width: $xlBreakpoint) {
  :deep(.product-image) {
    max-height: 330px;
  }
}

@media screen and (min-width: $xxlBreakpoint) {
  :deep(.product-image) {
    max-height: 371px;
  }
}

@media screen and (min-width: $xxxlBreakpoint) {
  :deep(.product-image) {
    max-height: 470px;
  }
}

.productBox {
  margin-bottom: 20px;

  @media screen and (min-width: $lgBreakpoint) {
    height: auto;
  }

  @media screen and (min-width: $xlBreakpoint) {
    height: 405px;
  }

  @media screen and (min-width: $xxlBreakpoint) {
    height: 445px;
  }

  @media screen and (min-width: $xxxlBreakpoint) {
    height: 545px;
  }
}

:deep(.product-image) {
  height: fit-content;
}
</style>
